import { Card, Container, Stack, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';

/**
 * Shared page-level CSS for the auth shell (background wash only — see the
 * 2026-08-25 revision note below for why there's no watermark layer). A
 * plain injected `<style>` tag rather than a `.module.css` file: this
 * component is one of a handful this redesign is scoped to touch (see
 * CLAUDE.md's file allowlist for the auth-screens task, 2026-08-25), and
 * Mantine v8 has no runtime `sx`-style prop for arbitrary CSS (pseudo-
 * elements, scheme-conditional selectors) — only inline `style` (no
 * pseudo-selectors) or a stylesheet. Scoped under the `.zs-auth-*` prefix so
 * it can't collide with anything else in the app. Dark-mode variants use
 * Mantine's `[data-mantine-color-scheme]` attribute selector (set on the
 * document root by MantineProvider) rather than the CSS `light-dark()`
 * function, since the latter isn't supported on the older Android WebViews
 * some gatehouse tablets still run.
 */
const AUTH_SHELL_STYLES = `
.zs-auth-shell {
  position: relative;
  min-height: 100dvh;
  width: 100%;
  overflow: hidden;
  isolation: isolate;
}

/* Faint radial wash behind the card — graphite-tinted, not a brand-color
   decoration (branded-neutral ruling, 2026-08-24: stripe colors stay
   functional-only). Subtle on purpose: this is chrome, not a hero banner. */
.zs-auth-shell::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: radial-gradient(ellipse 900px 520px at 50% -8%, rgba(37, 43, 46, 0.07), transparent 65%);
}

[data-mantine-color-scheme='dark'] .zs-auth-shell::before {
  background: radial-gradient(ellipse 900px 520px at 50% -8%, rgba(255, 255, 255, 0.05), transparent 65%);
}

.zs-auth-shell__container {
  position: relative;
  z-index: 1;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
}
`;

/**
 * Shared shell for auth-flow screens (login, tenant picker, callback landing).
 * Only auth screens use this component (checked, 2026-08-24), so the brand
 * mark lives here once rather than in each caller — signed-out login and the
 * "Choose a tenant" chooser both carry it (branded-neutral ruling,
 * 2026-08-24: logo in chrome, working surfaces stay neutral).
 *
 * 2026-08-25 redesign, round 2 (coordinator's rendered-PNG review of round 1
 * found real bugs, not nitpicks — noted here so nobody reintroduces them):
 *  - The card now uses `/brand/zebra-mark.png` (185x185, mark only) instead
 *    of `/brand/zebra-logo.png` (the tall stacked "Z" + ZEBRA + RENEWABLES
 *    wordmark) — at any width that reads as a small chrome mark the full
 *    wordmark is ~430px TALL and dominates the card exactly as before.
 *  - The page-background watermark (a second, oversized copy of the
 *    wordmark) is gone entirely, not just dimmed. It was rendering as a
 *    visible pale rectangle poking above the card — likely the logo PNG's
 *    own opaque canvas showing through at low opacity as a flat-edged box,
 *    not a soft texture — and with the card itself now also carrying a
 *    mark, a second background copy was one brand mention too many either
 *    way ("brand soup": card + watermark + typographic title, three times).
 *  - The accent bar is a real `borderTop` on the Card, not a `Card.Section`.
 *    The Card.Section approach looked correct on paper (Mantine clips a
 *    first-child section to the card's rounded corners via the card's own
 *    `overflow: hidden`) but rendered inset with a visible white gap above
 *    it — root cause: Card.Section's flush-top math subtracts
 *    `--card-padding`, a Card-specific CSS variable driven by the `padding`
 *    prop, but this component set spacing via the generic `p="xl"` style
 *    prop instead, which never touches `--card-padding` — so the section's
 *    negative margin (-md) undershot the actual applied padding (xl),
 *    leaving the gap. A plain `border-top` sidesteps that variable entirely
 *    and simply follows `radius="lg"` the way any bordered box does.
 */
export function CenteredCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="zs-auth-shell">
      <style>{AUTH_SHELL_STYLES}</style>
      <Container size="xs" className="zs-auth-shell__container">
        <Card
          withBorder
          radius="lg"
          shadow="lg"
          p="xl"
          w="100%"
          maw={440}
          style={{ borderTop: '3px solid var(--mantine-primary-color-filled)' }}
        >
          <Stack gap="lg">
            <Stack align="center" gap={6}>
              {/* Intrinsic width/height (the file's actual 185x185 pixel size)
                  let the browser reserve the right aspect ratio before the
                  image loads — the CSS `maxWidth`/`width` below still control
                  the rendered size, this just kills the layout shift on a
                  slow fetch. A small square mark under the real (typographic)
                  title, not a second headline competing with it.
                  `height: 'auto'` is NOT optional here (found the hard way,
                  2026-08-25 round 2): the HTML `height` attribute becomes a
                  low-priority CSS "presentational hint" of `height: 185px`,
                  and since nothing in `style` was overriding it, that hint
                  won over the intrinsic aspect-ratio despite `maxWidth`
                  correctly clamping the width — the image rendered squashed
                  into a 60x185 box instead of scaling to 60x60. This exact
                  bug, on the old 500x427 wordmark with no `height: 'auto'`
                  either, is *why* that logo rendered ~430px tall in the
                  first place (the original "horrific" complaint, and still
                  wrong after round 1's width-only shrink to 120px). */}
              <img
                src="/brand/zebra-mark.png"
                alt="Zebra Renewables"
                width={185}
                height={185}
                style={{ maxWidth: 60, width: '100%', height: 'auto' }}
              />
              <Title order={2} ta="center">
                {title}
              </Title>
              {subtitle && (
                <Text c="dimmed" size="sm" ta="center">
                  {subtitle}
                </Text>
              )}
            </Stack>
            {children}
          </Stack>
        </Card>
      </Container>
    </div>
  );
}
